import React, { useEffect, useRef, useState } from "react";
import { CheckCircle2, Eye, RefreshCw, Upload, XCircle } from "lucide-react";
import { toast } from "sonner";

import { api, fmtDateShort, fmtIDR, formatApiError } from "../lib/api";
import { PageHeader, Panel } from "../lib/ui";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { TransferProofThumbnail } from "../components/TransferProofThumbnail";

const STATUS = {
  matched: ["Cocok Otomatis", "text-status-passed"],
  matched_manual: ["Cocok Manual", "text-status-passed"],
  needs_review: ["Perlu Ditinjau", "text-amber-700"],
  unmatched: ["Belum Cocok", "text-status-reject"],
  ignored_debit: ["Debit Diabaikan", "text-wmuted"],
};

const money = (cents) => fmtIDR((Number(cents) || 0) / 100);

function Status({ value }) {
  const [label, tone] = STATUS[value] || [value || "-", "text-wmuted"];
  return <span className={`text-xs font-semibold ${tone}`}>{label}</span>;
}

export default function BcaReconciliation() {
  const fileRef = useRef();
  const [batches, setBatches] = useState([]);
  const [detail, setDetail] = useState(null);
  const [status, setStatus] = useState("all");
  const [uploading, setUploading] = useState(false);
  const [choices, setChoices] = useState({});

  const loadBatches = () => {
    api.get("/finance/bca-reconciliation/batches")
      .then((response) => setBatches(response.data))
      .catch((error) => toast.error(formatApiError(error.response?.data?.detail)));
  };

  const openBatch = async (batch, nextStatus = "all") => {
    try {
      const response = await api.get(`/finance/bca-reconciliation/batches/${batch.id}`, {
        params: { status: nextStatus },
      });
      setStatus(nextStatus);
      setDetail(response.data);
      setChoices({});
    } catch (error) {
      toast.error(formatApiError(error.response?.data?.detail));
    }
  };

  useEffect(() => { loadBatches(); }, []);

  const upload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const response = await api.post("/finance/bca-reconciliation/upload", form);
      toast.success(`${response.data.row_count} mutasi BCA berhasil diproses`);
      loadBatches();
      await openBatch(response.data);
    } catch (error) {
      toast.error(formatApiError(error.response?.data?.detail));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const rematch = async () => {
    try {
      const response = await api.post(`/finance/bca-reconciliation/batches/${detail.batch.id}/rematch`);
      toast.success(`${response.data.summary.matched} mutasi cocok otomatis`);
      loadBatches();
      openBatch(detail.batch, status);
    } catch (error) {
      toast.error(formatApiError(error.response?.data?.detail));
    }
  };

  const applyManualMatch = async (row) => {
    const saleId = choices[row.id];
    if (!saleId) {
      toast.error("Pilih transaksi kasir BCA terlebih dahulu");
      return;
    }
    try {
      await api.put(
        `/finance/bca-reconciliation/batches/${detail.batch.id}/rows/${row.id}/match`,
        { sale_id: saleId },
      );
      toast.success("Mutasi BCA dicocokkan manual");
      loadBatches();
      openBatch(detail.batch, status);
    } catch (error) {
      toast.error(formatApiError(error.response?.data?.detail));
    }
  };

  return (
    <div data-testid="bca-reconciliation-page">
      <PageHeader
        title="Rekonsiliasi BCA"
        subtitle="Cocokkan mutasi kredit BCA dengan pembayaran kasir BCA berdasarkan nominal dan tanggal ±1 hari."
      >
        <input
          ref={fileRef}
          accept=".csv,text/csv"
          className="hidden"
          data-testid="bca-csv-file-input"
          onChange={upload}
          type="file"
        />
        <Button
          className="bg-cocoa text-ivory"
          data-testid="bca-upload-button"
          disabled={uploading}
          onClick={() => fileRef.current?.click()}
        >
          <Upload className="w-4 h-4 mr-1.5" />
          {uploading ? "Memproses CSV..." : "Import Mutasi BCA"}
        </Button>
      </PageHeader>

      <Panel>
        <div className="flex items-center justify-between gap-3 mb-4">
          <div>
            <h2 className="font-heading font-bold text-cocoa">Batch Mutasi BCA</h2>
            <p className="text-sm text-wmuted">Import CSV disimpan agar dapat diaudit dan dicocokkan ulang.</p>
          </div>
        </div>
        <div className="space-y-3 md:hidden">
          {batches.map((batch) => (
            <div
              key={batch.id}
              className="border border-taupe/20 bg-white p-3"
              data-testid={`bca-batch-card-${batch.id}`}
            >
              <div className="font-medium text-cocoa truncate">{batch.filename}</div>
              <div className="mt-1 text-xs text-wmuted">
                {fmtDateShort(batch.period_start)} – {fmtDateShort(batch.period_end)}
              </div>
              <div className="grid grid-cols-2 gap-x-3 gap-y-1 mt-3 text-sm">
                <span className="text-wmuted">Kredit</span>
                <span className="text-right font-semibold">{money(batch.summary?.credit_total_cents)}</span>
                <span className="text-wmuted">Cocok / Review</span>
                <span className="text-right">
                  <span className="text-status-passed">{batch.summary?.matched || 0}</span>
                  {" / "}
                  <span className="text-amber-700">{batch.summary?.needs_review || 0}</span>
                </span>
                <span className="text-wmuted">Belum Cocok</span>
                <span className="text-right text-status-reject">{batch.summary?.unmatched || 0}</span>
              </div>
              <Button
                className="mt-3 w-full"
                data-testid={`bca-open-batch-card-${batch.id}`}
                onClick={() => openBatch(batch)}
                size="sm"
                variant="outline"
              >
                <Eye className="w-4 h-4 mr-1.5" />Lihat Hasil
              </Button>
            </div>
          ))}
          {!batches.length && <div className="py-10 text-center text-wmuted">Belum ada mutasi BCA yang diimport.</div>}
        </div>
        <div className="hidden md:block overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead>File</TableHead>
                <TableHead>Periode</TableHead>
                <TableHead className="text-right">Kredit</TableHead>
                <TableHead className="text-center">Cocok</TableHead>
                <TableHead className="text-center">Review</TableHead>
                <TableHead className="text-center">Belum Cocok</TableHead>
                <TableHead className="text-right">Aksi</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {batches.map((batch) => (
                <TableRow key={batch.id} data-testid={`bca-batch-row-${batch.id}`}>
                  <TableCell className="max-w-[240px] truncate">{batch.filename}</TableCell>
                  <TableCell>{fmtDateShort(batch.period_start)} – {fmtDateShort(batch.period_end)}</TableCell>
                  <TableCell className="text-right">{money(batch.summary?.credit_total_cents)}</TableCell>
                  <TableCell className="text-center text-status-passed">{batch.summary?.matched || 0}</TableCell>
                  <TableCell className="text-center text-amber-700">{batch.summary?.needs_review || 0}</TableCell>
                  <TableCell className="text-center text-status-reject">{batch.summary?.unmatched || 0}</TableCell>
                  <TableCell className="text-right">
                    <Button
                      data-testid={`bca-open-batch-${batch.id}`}
                      onClick={() => openBatch(batch)}
                      size="icon"
                      variant="ghost"
                    >
                      <Eye className="w-4 h-4 text-cocoa" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
              {!batches.length && <TableRow><TableCell colSpan={7} className="py-10 text-center text-wmuted">Belum ada mutasi BCA yang diimport.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </div>
      </Panel>

      {detail && (
        <BatchDetail
          choices={choices}
          detail={detail}
          onClose={() => setDetail(null)}
          onManualMatch={applyManualMatch}
          onRematch={rematch}
          onStatusChange={(nextStatus) => openBatch(detail.batch, nextStatus)}
          setChoices={setChoices}
          status={status}
        />
      )}
    </div>
  );
}

function BatchDetail({ choices, detail, onClose, onManualMatch, onRematch, onStatusChange, setChoices, status }) {
  const summary = detail.batch.summary || {};
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-6xl" data-testid="bca-batch-detail-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-cocoa">Hasil Rekonsiliasi BCA</DialogTitle>
          <DialogDescription>{detail.batch.filename} · {detail.batch.row_count} baris mutasi</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          <Stat label="Kredit" value={money(summary.credit_total_cents)} />
          <Stat label="Cocok" tone="text-status-passed" value={summary.matched || 0} />
          <Stat label="Review" tone="text-amber-700" value={summary.needs_review || 0} />
          <Stat label="Belum Cocok" tone="text-status-reject" value={summary.unmatched || 0} />
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Select onValueChange={onStatusChange} value={status}>
            <SelectTrigger className="w-[185px]" data-testid="bca-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Semua Status</SelectItem>
              <SelectItem value="matched">Cocok Otomatis</SelectItem>
              <SelectItem value="matched_manual">Cocok Manual</SelectItem>
              <SelectItem value="needs_review">Perlu Ditinjau</SelectItem>
              <SelectItem value="unmatched">Belum Cocok</SelectItem>
              <SelectItem value="ignored_debit">Debit Diabaikan</SelectItem>
            </SelectContent>
          </Select>
          <Button data-testid="bca-rematch-button" onClick={onRematch} size="sm" variant="outline">
            <RefreshCw className="w-4 h-4 mr-1.5" />Cocokkan Ulang
          </Button>
        </div>
        <div className="max-h-[48vh] overflow-auto border border-taupe/20">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead>Tanggal</TableHead>
                <TableHead>Keterangan BCA</TableHead>
                <TableHead className="text-right">Kredit/Debit</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Sales Kasir</TableHead>
                <TableHead className="text-center">Bukti</TableHead>
                <TableHead className="text-right">Tindakan</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {detail.rows.map((row) => (
                <TableRow key={row.id} data-testid={`bca-row-${row.id}`}>
                  <TableCell className="whitespace-nowrap">{fmtDateShort(row.transaction_date)}</TableCell>
                  <TableCell className="max-w-[290px] text-xs"><div className="truncate" title={row.description}>{row.description}</div><div className="text-wmuted">{row.match_note}</div></TableCell>
                  <TableCell className={`text-right font-semibold ${row.direction === "CR" ? "text-status-passed" : "text-wmuted"}`}>{row.direction} {money(row.amount_cents)}</TableCell>
                  <TableCell><Status value={row.match_status} /></TableCell>
                  <TableCell>{row.matched_sale_number || "-"}</TableCell>
                  <TableCell className="text-center">
                    <div className="flex justify-center">
                      <TransferProofThumbnail
                        available={row.transfer_proof_available}
                        saleId={row.matched_sale_id}
                        testId={`bca-transfer-proof-${row.id}`}
                      />
                    </div>
                  </TableCell>
                  <TableCell className="text-right">
                    {row.match_status === "needs_review" && row.candidates?.length > 0 && (
                      <div className="flex justify-end gap-1">
                        <Select onValueChange={(value) => setChoices({ ...choices, [row.id]: value })} value={choices[row.id] || ""}>
                          <SelectTrigger className="h-8 w-[145px]" data-testid={`bca-candidate-${row.id}`}><SelectValue placeholder="Pilih Sales" /></SelectTrigger>
                          <SelectContent>{row.candidates.map((candidate) => <SelectItem key={candidate.sale_id} value={candidate.sale_id}>{candidate.sale_number}</SelectItem>)}</SelectContent>
                        </Select>
                        <Button data-testid={`bca-manual-match-${row.id}`} onClick={() => onManualMatch(row)} size="sm"><CheckCircle2 className="w-4 h-4" /></Button>
                      </div>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {!detail.rows.length && <TableRow><TableCell colSpan={7} className="py-8 text-center text-wmuted">Tidak ada mutasi dengan filter ini.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Stat({ label, tone = "text-cocoa", value }) {
  return (
    <div className="border border-taupe/20 px-3 py-2" data-testid={`bca-stat-${label.toLowerCase().replace(" ", "-")}`}>
      <div className="text-[11px] uppercase text-wmuted">{label}</div>
      <div className={`text-base font-bold ${tone}`}>{value}</div>
    </div>
  );
}